/* eslint-disable react-refresh/only-export-components */
"use client";

import { cleanupEvent, EventConstants, listenEvent } from "@/lib/event-emitter";
import { ErrorNotificationType } from "@/pages/components/error-notification";
import type React from "react";
import {
  createContext,
  useContext,
  useState,
  useCallback,
  useEffect,
  useRef,
} from "react";

export type ErrorNotification = {
  id: string;
  message: string;
  type?: "error" | "warning" | "info";
  duration?: number; // in milliseconds, 0 means no auto-dismiss
};

type ErrorContextType = {
  errors: ErrorNotification[];
  addError: (error: Omit<ErrorNotification, "id">) => void;
  removeError: (id: string) => void;
  clearAllErrors: () => void;
};

const ErrorContext = createContext<ErrorContextType | undefined>(undefined);

export function ErrorProvider({ children }: { children: React.ReactNode }) {
  const [errors, setErrors] = useState<ErrorNotification[]>([]);

  // Use ref to avoid stale closures
  const addErrorRef = useRef<(error: Omit<ErrorNotification, "id">) => void>();

  const addError = useCallback((error: Omit<ErrorNotification, "id">) => {
    const id = Math.random().toString(36).substr(2, 9);
    const newError: ErrorNotification = {
      id,
      type: error.type || ErrorNotificationType.ERROR,
      duration: error.duration || 5000, // default 5 seconds
      message: error.message || "An error occurred.<error-notification>",
    };

    setErrors((prev) => [...prev, newError]);

    // Auto-dismiss if duration is set and > 0
    if (newError.duration && newError.duration > 0) {
      setTimeout(() => {
        removeError(id);
      }, newError.duration);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Keep ref updated
  addErrorRef.current = addError;

  const removeError = useCallback((id: string) => {
    setErrors((prev) => prev.filter((error) => error.id !== id));
  }, []);

  const clearAllErrors = useCallback(() => {
    setErrors([]);
  }, []);

  useEffect(() => {
    listenEvent(EventConstants.ADD_ERROR, (event) => {
      // Use ref to get current addError function
      addErrorRef.current?.({
        message: event?.detail.message,
        type: event?.detail.type,
      });
    });

    return () => cleanupEvent();
  }, []); // Now safe with empty dependency array

  return (
    <ErrorContext.Provider
      value={{ errors, addError, removeError, clearAllErrors }}
    >
      {children}
    </ErrorContext.Provider>
  );
}

export function useNotificationError() {
  const context = useContext(ErrorContext);
  if (context === undefined) {
    throw new Error("useError must be used within an ErrorProvider");
  }
  return context;
}
